import React, { forwardRef, useRef, useState, useEffect, type ChangeEvent } from 'react';
import { Add, Subtract } from '@core/icons-react';
import { InputWrapper } from '../shared/InputWrapper/InputWrapper';
import { useNormalizedInputProps } from '../shared/hooks/useNormalizedInputProps';
import { textInputProps } from '../shared/utils/textInputProps';
import type { BaseInputProps } from '../shared/types/input.types';

export interface NumberInputProps extends Omit<BaseInputProps<HTMLInputElement>, 'onChange' | 'value' | 'defaultValue'> {
  /**
   * Specify the value of the input
   */
  value?: number | string;
  
  /**
   * Specify the default value of the input
   */
  defaultValue?: number | string;
  
  /**
   * Optionally provide an `onChange` handler that is called whenever input is updated
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>, value: number | string) => void;
  
  /**
   * Optional handler for the `onEmptyValue` event
   */
  onEmptyValue?: () => void;
  
  /**
   * Specify if the control should allow empty string values
   */
  allowEmpty?: boolean;
  
  /**
   * Maximum value allowed
   */
  max?: number;
  
  /**
   * Minimum value allowed
   */
  min?: number;
  
  /**
   * Specify how much the values should increase/decrease upon clicking on up/down buttons
   */
  step?: number;
  
  /**
   * Specify whether to show the increment/decrement buttons
   */
  hideSteppers?: boolean;
  
  /**
   * `true` to allow invalid characters in the input, `false` to prevent
   */
  invalidCharacters?: RegExp;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  {
    className,
    inputClassName,
    disabled = false,
    helperText,
    hideLabel,
    hideSteppers = false,
    id,
    inline = false,
    invalid = false,
    invalidText,
    invalidCharacters = /[^\d-+.,e]/gi,
    labelText,
    max,
    min,
    onChange = () => {},
    onClick = () => {},
    onEmptyValue,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    readOnly,
    required,
    size = 'md',
    step = 1,
    value,
    defaultValue,
    warn = false,
    warnText,
    allowEmpty = false,
    ...rest
  },
  ref
) {
  const prefix = 'core';
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = ref || inputRef;
  
  const [currentValue, setCurrentValue] = useState<string | number>(
    value !== undefined ? value : defaultValue !== undefined ? defaultValue : ''
  );
  
  useEffect(() => {
    if (value !== undefined) {
      setCurrentValue(value);
    }
  }, [value]);
  
  const normalizedProps = useNormalizedInputProps({
    id,
    readOnly,
    disabled,
    invalid,
    invalidText,
    warn,
    warnText,
  });
  
  const numberInputClasses = [
    `${prefix}-number-input__input`,
    normalizedProps.invalid && `${prefix}-number-input__input--invalid`,
    normalizedProps.warn && `${prefix}-number-input__input--warning`,
    size && `${prefix}-number-input__input--${size}`,
    hideSteppers && `${prefix}-number-input__input--no-steppers`,
    inputClassName,
  ].filter(Boolean).join(' ');
  
  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    if (!normalizedProps.disabled && !readOnly) {
      const value = evt.target.value;
      
      // Remove invalid characters
      const cleanedValue = invalidCharacters ? value.replace(invalidCharacters, '') : value;
      
      if (cleanedValue !== value) {
        evt.target.value = cleanedValue;
      }
      
      if (cleanedValue === '' && !allowEmpty) {
        if (onEmptyValue) {
          onEmptyValue();
        }
      } else {
        setCurrentValue(cleanedValue);
        onChange(evt, cleanedValue);
      }
    }
  };
  
  const handleIncrement = () => {
    if (!disabled && !readOnly) {
      const numValue = typeof currentValue === 'number' ? currentValue : parseFloat(currentValue as string) || 0;
      const newValue = max !== undefined ? Math.min(numValue + step, max) : numValue + step;
      setCurrentValue(newValue);
      
      // Create synthetic event
      const event = {
        target: { value: newValue.toString() }
      } as ChangeEvent<HTMLInputElement>;
      onChange(event, newValue);
    }
  };
  
  const handleDecrement = () => {
    if (!disabled && !readOnly) {
      const numValue = typeof currentValue === 'number' ? currentValue : parseFloat(currentValue as string) || 0;
      const newValue = min !== undefined ? Math.max(numValue - step, min) : numValue - step;
      setCurrentValue(newValue);
      
      // Create synthetic event
      const event = {
        target: { value: newValue.toString() }
      } as ChangeEvent<HTMLInputElement>;
      onChange(event, newValue);
    }
  };
  
  const sharedNumberInputProps = {
    id,
    onChange: handleChange,
    onClick: (evt: React.MouseEvent<HTMLInputElement>) => {
      if (!normalizedProps.disabled) {
        onClick(evt);
      }
    },
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    type: 'text', // Use text type to have more control
    inputMode: 'numeric' as const,
    pattern: '[0-9]*',
    ref: mergedRef,
    className: numberInputClasses,
    title: placeholder,
    disabled: normalizedProps.disabled,
    readOnly,
    required,
    value: currentValue,
    max,
    min,
    step,
    'aria-describedby': helperText ? normalizedProps.helperId : undefined,
    'data-invalid': normalizedProps.invalid || undefined,
    ...rest,
  };
  
  const input = (
    <div className={`${prefix}-number-input__controls`}>
      <input
        {...textInputProps({
          sharedTextInputProps: sharedNumberInputProps,
          invalid: normalizedProps.invalid,
          invalidId: normalizedProps.invalidId,
          warn: normalizedProps.warn,
          warnId: normalizedProps.warnId,
        })}
      />
      {!hideSteppers && (
        <div className={`${prefix}-number-input__buttons`}>
          <button
            type="button"
            className={`${prefix}-number-input__button ${prefix}-number-input__button--up`}
            disabled={disabled || readOnly || (max !== undefined && Number(currentValue) >= max)}
            onClick={handleIncrement}
            tabIndex={-1}
            aria-label="Increment"
            title="Increment"
          >
            <Add size={16} />
          </button>
          <button
            type="button"
            className={`${prefix}-number-input__button ${prefix}-number-input__button--down`}
            disabled={disabled || readOnly || (min !== undefined && Number(currentValue) <= min)}
            onClick={handleDecrement}
            tabIndex={-1}
            aria-label="Decrement"
            title="Decrement"
          >
            <Subtract size={16} />
          </button>
        </div>
      )}
    </div>
  );
  
  const numberInputWrapperClasses = [
    `${prefix}-number-input-wrapper`,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <InputWrapper
      className={numberInputWrapperClasses}
      disabled={normalizedProps.disabled}
      helperText={helperText}
      hideLabel={hideLabel}
      id={id}
      inline={inline}
      invalid={normalizedProps.invalid}
      invalidText={invalidText}
      labelText={labelText}
      readOnly={readOnly}
      required={required}
      size={size}
      warn={normalizedProps.warn}
      warnText={warnText}
    >
      {input}
    </InputWrapper>
  );
});

NumberInput.displayName = 'NumberInput';
